<template>
    <div
        class="entry-chat-host"
        @click.stop
        @mousedown.stop
        @pointerdown.stop
        @touchstart.stop
        @keydown.enter.stop
        @keydown.space.stop
    >
        <div v-if="loading" class="text-caption text-medium-emphasis px-1 py-3">
            {{ $t("reservations.chat.loading") }}
        </div>
        <div v-else-if="meta && !meta.can_chat" class="text-caption text-medium-emphasis px-1 py-3">
            {{ $t("entries.chat.locked") }}
        </div>
        <div v-else-if="showEmpty" class="entry-chat-thread-list rounded border pa-3 mt-3">
            <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-body-1 font-weight-bold">{{ $t("reservations.chat.title") }}</span>
                <v-btn class="entry-chat-tool-btn" icon size="x-small" variant="text" @click="$emit('close')">
                    <v-icon size="22">mdi-close</v-icon>
                </v-btn>
            </div>
            <div class="text-caption text-medium-emphasis">
                {{ $t("entries.chat.noMessagesYet") }}
            </div>
        </div>
        <template v-else-if="meta">
            <v-tabs
                v-if="showTabs"
                v-model="selectedThreadUid"
                class="entry-chat-tabs"
                density="compact"
                bg-color="transparent"
                color="primary"
                slider-color="primary"
                height="36"
                show-arrows
            >
                <v-tab
                    v-for="thread in orderedThreads"
                    :key="thread.uid"
                    :value="thread.uid"
                    rounded="0"
                    elevation="0"
                    variant="text"
                        size="default"
                        class="text-none"
                    >
                    <span class="entry-chat-tab-label">
                        <span class="entry-chat-tab-side" aria-hidden="true"></span>
                        <span class="entry-chat-tab-name">{{ thread.counterpart_company?.name }}</span>
                        <span class="entry-chat-tab-side">
                            <v-slide-x-reverse-transition>
                                <v-icon
                                    v-if="thread.reservation_uid"
                                    size="16"
                                    color="success"
                                    aria-hidden="true"
                                >mdi-check-circle</v-icon>
                            </v-slide-x-reverse-transition>
                            <span
                                v-if="showThreadBadge(thread)"
                                class="entry-chat-unread-dot"
                            />
                        </span>
                    </span>
                </v-tab>
            </v-tabs>
            <ReservationChatPanel
                v-if="showConversation"
                :expanded="true"
                :allow-attachments="false"
                :threads="orderedThreads"
                :badge-hold-uid="badgeHoldUid || ''"
                :reservation-uid="activeReservationUid"
                :entry-type="entry.form_type"
                :entry-uid="String(entry.uid)"
                :thread-uid="activeThreadUid"
                :notice-code="activeNotice"
                :viewer-is-owner="!!meta.is_owner"
                :viewer-email="meta.viewer_email || ''"
                :reservation-number="meta.identification_id || ''"
                @update:expanded="onPanelExpanded"
                @update:thread-uid="onThreadUid"
                @linked="onLinked"
                @moved="onMoved"
                @removed="onRemoved"
                @sent="$emit('activity')"
                @read="onRead"
                @notice="onLiveNotice"
            />
        </template>
    </div>
</template>

<script>
import api from "@/api";
import ReservationChatPanel from "@/components/reservation/ReservationChatPanel.vue";
import emitter from "@/eventBus";

export default {
    name: "EntryChatPanel",
    components: { ReservationChatPanel },
    props: {
        entry: { type: Object, required: true },
    },
    emits: ["close", "activity"],
    data() {
        return {
            loading: false,
            meta: null,
            selectedThreadUid: null,
            linkedReservationUid: "",
            clearedThreadUids: new Set(),
            tabOrder: [],
            badgeHoldUid: null,
            pendingBadgeHoldUid: null,
            badgeHoldTimer: null,
        };
    },
    computed: {
        threads() {
            return this.meta?.threads || [];
        },
        orderedThreads() {
            const byUid = new Map(this.threads.map((thread) => [thread.uid, thread]));
            const ordered = this.tabOrder.map((uid) => byUid.get(uid)).filter(Boolean);
            const known = new Set(ordered.map((thread) => thread.uid));
            for (const thread of this.threads) {
                if (!known.has(thread.uid)) ordered.push(thread);
            }
            return ordered;
        },
        selectedThread() {
            if (!this.selectedThreadUid) return null;
            return this.threads.find((thread) => thread.uid === this.selectedThreadUid) || null;
        },
        showTabs() {
            if (!this.meta?.is_owner || !this.threads.length) return false;
            if (this.threads.length > 1) return true;
            return Boolean(this.threads[0]?.reservation_uid);
        },
        showEmpty() {
            return Boolean(this.meta?.is_owner && this.meta?.can_chat && !this.threads.length);
        },
        showConversation() {
            if (this.meta?.chat_on_reservation) return false;
            if (!this.meta?.can_chat || this.showEmpty) return false;
            if (this.meta.is_owner) return Boolean(this.selectedThreadUid);
            return true;
        },
        activeReservationUid() {
            if (this.selectedThread?.reservation_uid) return "";
            if (!this.meta?.is_owner && this.linkedReservationUid) return this.linkedReservationUid;
            if (!this.meta?.is_owner && this.meta?.thread?.reservation_uid) {
                return this.meta.thread.reservation_uid;
            }
            return "";
        },
        activeThreadUid() {
            if (this.activeReservationUid) return "";
            if (this.selectedThread) return this.selectedThread.uid;
            if (!this.meta?.is_owner && this.meta?.thread?.uid) return this.meta.thread.uid;
            return "";
        },
        activeNotice() {
            if (this.selectedThread?.reservation_uid) return "";
            if (this.activeReservationUid) return "";
            if (this.selectedThread?.notice) return this.selectedThread.notice;
            return this.meta?.notice || "";
        },
    },
    watch: {
        "entry.uid"() {
            this.selectedThreadUid = null;
            this.linkedReservationUid = "";
            this.clearedThreadUids = new Set();
            this.tabOrder = [];
            this.clearBadgeHold();
            this.loadMeta();
        },
        "entry.chat_unread_count"() {
            this.refreshThreadUnread();
        },
        selectedThreadUid(uid) {
            if (uid && (uid === this.pendingBadgeHoldUid || uid === this.badgeHoldUid)) return;
            this.markThreadSeen(uid);
        },
    },
    mounted() {
        this.loadMeta();
        emitter.on("ws-update", this.onListingChatMoved);
    },
    beforeUnmount() {
        this.clearBadgeHold();
        emitter.off("ws-update", this.onListingChatMoved);
    },
    methods: {
        async loadMeta({ silent = false } = {}) {
            if (!this.entry?.uid || !this.entry?.form_type) return;
            if (!silent) this.loading = true;
            try {
                const { data } = await api.get(
                    `/entries/${this.entry.form_type}/${this.entry.uid}/chat/`
                );
                this.meta = data;
                if (data?.chat_on_reservation) {
                    this.$emit("close");
                    return;
                }
                if (!data?.is_owner && data?.thread?.reservation_uid) {
                    this.linkedReservationUid = data.thread.reservation_uid;
                }
                const initial = !silent && !this.tabOrder.length;
                if (initial) this.applyInitialSelection();
                else {
                    this.rebuildTabOrder(false);
                    this.ensureSelection();
                }
            } catch {
                if (!silent) this.meta = { can_chat: false, is_owner: false, threads: [] };
            } finally {
                if (!silent) this.loading = false;
            }
        },
        isOpenUnread(thread) {
            return Boolean(thread && !thread.reservation_uid && (thread.unread_count || 0) > 0);
        },
        threadActivity(thread) {
            return thread?.last_message_at || "";
        },
        sortByActivity(list) {
            return list.slice().sort((a, b) => this.threadActivity(b).localeCompare(this.threadActivity(a)));
        },
        rebuildTabOrder(initial) {
            const unread = this.sortByActivity(this.threads.filter((thread) => this.isOpenUnread(thread)));
            const rest = this.sortByActivity(this.threads.filter((thread) => !this.isOpenUnread(thread)));
            if (initial || !this.tabOrder.length) {
                this.tabOrder = [...unread, ...rest].map((thread) => thread.uid);
                return;
            }
            const freshIds = new Set(this.threads.map((thread) => thread.uid));
            const known = new Set(this.tabOrder);
            const next = this.tabOrder.filter((uid) => freshIds.has(uid));
            const newcomers = unread.filter((thread) => !known.has(thread.uid));
            for (const thread of newcomers.slice().reverse()) next.unshift(thread.uid);
            for (const thread of rest) {
                if (!known.has(thread.uid)) next.push(thread.uid);
            }
            this.tabOrder = next;
        },
        applyInitialSelection() {
            this.rebuildTabOrder(true);
            const first = this.orderedThreads[0];
            this.pendingBadgeHoldUid = first && this.isOpenUnread(first) ? first.uid : null;
            this.ensureSelection();
            if (this.pendingBadgeHoldUid && this.pendingBadgeHoldUid === this.selectedThreadUid) {
                this.badgeHoldUid = this.pendingBadgeHoldUid;
                this.clearBadgeHoldTimer();
                this.badgeHoldTimer = setTimeout(() => {
                    this.badgeHoldUid = null;
                    this.badgeHoldTimer = null;
                }, 1500);
            } else {
                this.markThreadSeen(this.selectedThreadUid);
            }
            this.pendingBadgeHoldUid = null;
        },
        ensureSelection() {
            if (!this.meta?.is_owner) return;
            const ordered = this.orderedThreads;
            const stillThere = ordered.some((thread) => thread.uid === this.selectedThreadUid);
            if (stillThere) return;
            if (!ordered.length) {
                this.selectedThreadUid = null;
                return;
            }
            const unread = ordered.find((thread) => this.isOpenUnread(thread));
            this.selectedThreadUid = (unread || ordered[0]).uid;
        },
        showThreadBadge(thread) {
            if (!thread || thread.reservation_uid) return false;
            if (thread.uid === this.badgeHoldUid) return true;
            return thread.unread_count > 0 && thread.uid !== this.selectedThreadUid;
        },
        clearBadgeHoldTimer() {
            if (this.badgeHoldTimer) {
                clearTimeout(this.badgeHoldTimer);
                this.badgeHoldTimer = null;
            }
        },
        clearBadgeHold() {
            this.clearBadgeHoldTimer();
            this.badgeHoldUid = null;
            this.pendingBadgeHoldUid = null;
        },
        onPanelExpanded(open) {
            if (!open) this.$emit("close");
        },
        onThreadUid(threadUid) {
            if (this.meta?.is_owner) {
                this.selectedThreadUid = threadUid;
                return;
            }
            if (!this.meta) return;
            this.meta = {
                ...this.meta,
                thread: {
                    ...(this.meta.thread || {}),
                    uid: threadUid,
                },
            };
        },
        markThreadSeen(uid) {
            if (!uid || uid === this.badgeHoldUid) return;
            const thread = this.threads.find((item) => item.uid === uid);
            if (!thread || thread.reservation_uid) return;
            this.clearedThreadUids.add(uid);
            thread.unread_count = 0;
        },
        async refreshThreadUnread() {
            if (!this.meta?.is_owner || !this.entry?.uid || !this.entry?.form_type || this.loading) return;
            try {
                const { data } = await api.get(
                    `/entries/${this.entry.form_type}/${this.entry.uid}/chat/`
                );
                const freshThreads = data?.threads || [];
                const merged = freshThreads.map((fresh) => {
                    const current = this.threads.find((item) => item.uid === fresh.uid);
                    const serverCount = fresh.unread_count || 0;
                    let unread = serverCount;
                    if (
                        fresh.uid === this.selectedThreadUid
                        || this.clearedThreadUids.has(fresh.uid)
                    ) {
                        if (serverCount === 0) this.clearedThreadUids.delete(fresh.uid);
                        unread = fresh.reservation_uid ? serverCount : 0;
                    }
                    return {
                        ...fresh,
                        unread_count: unread,
                        notice: current?.notice != null ? current.notice : fresh.notice,
                    };
                });
                this.meta = { ...this.meta, threads: merged };
                this.rebuildTabOrder(false);
                this.ensureSelection();
            } catch {
                /* ignore */
            }
        },
        onLiveNotice(payload) {
            const notice = payload?.notice || null;
            const thread = this.threads.find((item) => item.uid === payload?.threadUid);
            if (thread) thread.notice = notice;
            if (this.meta && !this.meta.is_owner) {
                this.meta = { ...this.meta, notice };
            }
        },
        onRead(readId) {
            const thread = this.threads.find(
                (item) => item.uid === readId || item.reservation_uid === readId
            );
            if (thread) {
                this.clearedThreadUids.delete(thread.uid);
                thread.unread_count = 0;
            }
            this.$emit("activity");
            this.refreshThreadUnread();
        },
        onListingChatMoved(data) {
            if (data?.type !== "entry_chat_moved" || String(data.entry_uid) !== String(this.entry?.uid)) return;
            const hadThreads = this.threads.length > 0;
            this.loadMeta({ silent: true }).then(() => {
                if (hadThreads && !this.threads.length && !this.meta?.chat_on_reservation) {
                    this.$emit("close");
                }
            });
        },
        onMoved(payload) {
            const reservationUid = payload?.reservationUid || "";
            const threadUid = payload?.threadUid || this.selectedThreadUid;
            if (!reservationUid || !threadUid) return;
            const thread = (this.meta?.threads || []).find((item) => item.uid === threadUid);
            if (thread) thread.reservation_uid = reservationUid;
            this.$emit("activity");
        },
        onLinked(payload) {
            const reservationUid = payload?.reservationUid || "";
            if (!reservationUid) return;
            if (this.meta?.is_owner) {
                this.onMoved({
                    reservationUid,
                    threadUid: payload?.threadUid || this.selectedThreadUid,
                });
                return;
            }
            this.$emit("close");
            this.$emit("activity");
        },
        async onRemoved() {
            this.selectedThreadUid = null;
            this.linkedReservationUid = "";
            this.tabOrder = [];
            await this.loadMeta();
            if (this.meta && !this.meta.can_chat) return;
            if (!this.meta?.is_owner) this.$emit("close");
            this.$emit("activity");
        },
    },
};
</script>

<style scoped>
.entry-chat-host {
    width: 100%;
}
.entry-chat-tool-btn {
    width: 28px;
    height: 28px;
}
.entry-chat-tabs {
    margin-top: 12px;
}
.entry-chat-tabs :deep(.v-tab),
.entry-chat-tabs :deep(.v-btn__content) {
    justify-content: center;
}
.entry-chat-tabs :deep(.v-btn__content) {
    width: 100%;
}
.entry-chat-tab-label {
    display: grid;
    grid-template-columns: 1rem auto 1rem;
    align-items: center;
    column-gap: 8px;
    width: 100%;
}
.entry-chat-tab-name {
    text-align: center;
}
.entry-chat-tab-side {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1rem;
}
.entry-chat-unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgb(var(--v-theme-error));
    flex-shrink: 0;
}
</style>
